/**
 * Own Orbit in-place image zoom.
 * The first click magnifies the CURRENT product image inside its gallery frame.
 * Another click, clicking a different image, or Escape resets it.
 * Desktop: moving the mouse pans the magnified image to the cursor.
 * Touch: first tap magnifies; drag pans; next tap resets. Native swipe remains
 * available until zoom starts. No dialog, navigation, or new image resources.
 */
const selector = 'media-gallery.oo-inline-zoom-enabled [data-oo-inline-zoom]';
const zoomFactor = 2.25;
let active = null;
let touchPan = null;
let suppressedClickTarget = null;
let suppressedClickUntil = 0;
let down = null;

function getSlide(node) {
  return node instanceof Element ? node.closest(selector) : null;
}
function getImage(slide) {
  return slide?.querySelector('img.product-media__image') ?? null;
}
function updateOrigin(slide, x, y) {
  slide.style.setProperty('--oo-zoom-x', `${Math.max(0, Math.min(100, x))}%`);
  slide.style.setProperty('--oo-zoom-y', `${Math.max(0, Math.min(100, y))}%`);
}
function positionFromEvent(slide, event) {
  const rect = slide.querySelector('.product-media')?.getBoundingClientRect() ?? slide.getBoundingClientRect();
  if (!rect.width || !rect.height || !event || !Number.isFinite(event.clientX)) return [50, 50];
  return [100 * (event.clientX - rect.left) / rect.width, 100 * (event.clientY - rect.top) / rect.height];
}
function reset() {
  if (!active) return;
  active.classList.remove('oo-inline-zoom-active', 'oo-inline-zoom-panning');
  active.querySelector('[data-oo-zoom-control]')?.setAttribute('aria-pressed', 'false');
  active.style.removeProperty('--oo-zoom-x');
  active.style.removeProperty('--oo-zoom-y');
  active = null;
  touchPan = null;
}
function toggle(slide, event) {
  if (!getImage(slide)) return;
  if (active === slide) {
    reset();
    return;
  }
  reset();
  active = slide;
  const [x, y] = positionFromEvent(slide, event);
  updateOrigin(slide, x, y);
  slide.classList.add('oo-inline-zoom-active');
  slide.querySelector('[data-oo-zoom-control]')?.setAttribute('aria-pressed', 'true');
}

// The theme's original on:click zoom-dialog handler was removed from slides.
// We only use delegated listeners so variant re-renders work without re-binding.
document.addEventListener('click', (event) => {
  const slide = getSlide(event.target);
  // A completed swipe/drag may synthesize one click. Suppress that click
  // only for the dragged slide, without blocking subsequent thumbnail taps.
  if (slide && slide === suppressedClickTarget && Date.now() < suppressedClickUntil) {
    suppressedClickTarget = null;
    return;
  }
  suppressedClickTarget = null;
  if (!slide) {
    if (active) reset();
    return;
  }
  if (event.target instanceof Element &&
      event.target.closest('a, input, select, textarea, video, model-viewer, [data-shopify-xr]')) return;
  // Native video / model controls never activate image zoom.
  if (!getImage(slide)) return;
  event.preventDefault();
  toggle(slide, event);
});

document.addEventListener('pointerdown', (event) => {
  const slide = getSlide(event.target);
  down = slide ? { slide, x: event.clientX, y: event.clientY, moved: false } : null;
  if (!active?.isConnected) reset();
  if (!active || active !== slide || event.pointerType !== 'touch') return;
  const [x, y] = positionFromEvent(slide, event);
  const ox = parseFloat(slide.style.getPropertyValue('--oo-zoom-x'));
  const oy = parseFloat(slide.style.getPropertyValue('--oo-zoom-y'));
  touchPan = { id: event.pointerId, x: event.clientX, y: event.clientY,
    originX: Number.isFinite(ox) ? ox : x,
    originY: Number.isFinite(oy) ? oy : y,
    moved: false };
  slide.setPointerCapture?.(event.pointerId);
}, { capture: true });

document.addEventListener('pointermove', (event) => {
  if (down && Math.hypot(event.clientX - down.x, event.clientY - down.y) > 9) down.moved = true;
  if (!active?.isConnected) { reset(); return; }
  if (!active) return;
  if (touchPan && event.pointerId === touchPan.id) {
    const dx = event.clientX - touchPan.x;
    const dy = event.clientY - touchPan.y;
    if (Math.hypot(dx, dy) > 7) touchPan.moved = true;
    if (!touchPan.moved) return;
    active.classList.add('oo-inline-zoom-panning');
    const r = active.querySelector('.product-media')?.getBoundingClientRect() ?? active.getBoundingClientRect();
    const originX = touchPan.originX - 100 * dx / (r.width * (zoomFactor - 1));
    const originY = touchPan.originY - 100 * dy / (r.height * (zoomFactor - 1));
    updateOrigin(active, originX, originY);
    event.preventDefault();
    event.stopPropagation();
  } else if (event.pointerType === 'mouse' && active.contains(event.target)) {
    const [x, y] = positionFromEvent(active, event);
    updateOrigin(active, x, y);
  }
}, { capture: true, passive: false });

function finishPointer(event) {
  if (touchPan && event.pointerId === touchPan.id) {
    if (touchPan.moved) {
      suppressedClickTarget = active;
      suppressedClickUntil = Date.now() + 420;
    }
    active?.classList.remove('oo-inline-zoom-panning');
    touchPan = null;
  }
  // Retain moved information for the synthetic click after a swipe.
  if (down?.moved) {
    suppressedClickTarget = down.slide;
    suppressedClickUntil = Date.now() + 420;
  }
  down = null;
}
document.addEventListener('pointerup', finishPointer, { capture: true });
document.addEventListener('pointercancel', finishPointer, { capture: true });
document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && active) {
    event.preventDefault();
    reset();
  }
});
// Selecting a thumbnail, a different variant, or navigating away must reset.
document.addEventListener('click', (event) => {
  if (!active || !(event.target instanceof Element)) return;
  if (event.target.closest('slideshow-controls, .carrosol-spacing-customization, .slideshow-controls__arrows, .variant-picker')) reset();
});
